import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  IInsurancePolicy,
  IPolicyTableRowProps,
  IRenewalTracking,
  TInvoiceStatus,
  TPolicyStatus,
  TRenewalStatus,
} from '@/@types/insurance';
import type { IColumnConfig } from '@/@types/tableColumnVisibility';
import { NotificationStatusPopover } from './NotificationStatusPopover';

const STATUS_VARIANTS: Record<TPolicyStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    draft: 'secondary',
    under_review: 'outline',
    rejected: 'destructive',
    active: 'default',
    expired: 'outline',
    cancelled: 'destructive',
  };

const STATUS_LABELS: Record<TPolicyStatus, string> = {
  draft: 'Draft',
  under_review: 'Under Review',
  rejected: 'Rejected',
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

function toTitleCase(name: string): string {
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const RENEWAL_STATUS_LABELS: Record<TRenewalStatus, string> = {
  unknown: 'Unknown',
  no_need: 'No need',
  in_progress: 'In progress',
  completed: 'Completed',
};

function RenewalColumn({
  tracking,
  onChange,
}: {
  tracking?: { renewalStatus: TRenewalStatus } | null;
  onChange: (s: TRenewalStatus) => void;
}) {
  return (
    <div
      className="w-[130px] shrink-0 pr-4"
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      <Select
        value={tracking?.renewalStatus ?? 'unknown'}
        onValueChange={val => onChange(val as TRenewalStatus)}
      >
        {/* AS_JUSTIFIED: Select returns string, TRenewalStatus values are the only options */}
        <SelectTrigger className="h-8 text-xs w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(RENEWAL_STATUS_LABELS) as TRenewalStatus[]).map(s => (
            <SelectItem key={s} value={s}>
              {RENEWAL_STATUS_LABELS[s]}
            </SelectItem>
          ))}
          {/* AS_JUSTIFIED: Object.keys returns string[], cast to known enum keys */}
        </SelectContent>
      </Select>
    </div>
  );
}

function renderPolicyCell(
  col: IColumnConfig,
  policy: IInsurancePolicy,
  onInvoiceStatusChange: (status: TInvoiceStatus) => void,
  renewalTracking: IRenewalTracking | null | undefined,
  onRenewalStatusChange: ((status: TRenewalStatus) => void) | undefined
): React.ReactNode {
  switch (col.id) {
    case 'policyNumber':
      return (
        <span className="text-sm font-medium text-foreground truncate block">
          {policy.policyNumber}
        </span>
      );
    case 'customer':
      return (
        <span className="text-sm text-foreground truncate block">
          {policy.customerName ? (
            toTitleCase(policy.customerName)
          ) : (
            <span className="text-muted-foreground italic">No customer</span>
          )}
        </span>
      );
    case 'provider':
      return (
        <span className="text-sm text-muted-foreground truncate block">
          {policy.providerName ?? '—'}
        </span>
      );
    case 'type':
      return (
        <span className="text-sm text-muted-foreground truncate block">
          {policy.insuranceTypeName ?? '—'}
        </span>
      );
    case 'status':
      return (
        <div className="flex justify-center">
          <Badge
            variant={STATUS_VARIANTS[policy.status]}
            className={cn(
              'w-[120px] justify-center',
              policy.status === 'under_review' &&
                'border-transparent bg-[var(--accent-orange-bg)] text-[var(--accent-orange)]'
            )}
          >
            {STATUS_LABELS[policy.status]}
          </Badge>
        </div>
      );
    case 'premium':
      return (
        <span className="text-sm text-foreground tabular-nums">
          {policy.premium ? `${policy.premium.toLocaleString()}` : '—'}
        </span>
      );
    case 'invoice':
      return (
        <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
          <Select
            value={policy.invoiceStatus ?? 'not_paid'}
            onValueChange={val => onInvoiceStatusChange(val as TInvoiceStatus)} // AS_JUSTIFIED: Select onValueChange returns string, TInvoiceStatus values are the only options
          >
            <SelectTrigger className="h-8 text-xs w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not_paid">Not paid</SelectItem>
              <SelectItem value="partially_paid">Partially paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    case 'renewal':
      return (
        <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
          {onRenewalStatusChange ? (
            <RenewalColumn tracking={renewalTracking} onChange={onRenewalStatusChange} />
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
      );
    case 'notificationStatus':
      return (
        <div
          className="flex items-center"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <NotificationStatusPopover tracking={renewalTracking ?? null} />
        </div>
      );
    case 'createdBy':
      return (
        <span className="text-sm text-muted-foreground truncate block">
          {policy.creatorName ?? '—'}
        </span>
      );
    case 'issued':
      return (
        <span className="text-sm text-muted-foreground">
          {policy.status === 'draft' ? '—' : policy.issuedAt ? formatDate(policy.issuedAt) : '—'}
        </span>
      );
    case 'expires':
      return (
        <span className="text-sm text-muted-foreground">
          {policy.status === 'draft' ? '—' : policy.expiresAt ? formatDate(policy.expiresAt) : '—'}
        </span>
      );
    default:
      return null;
  }
}

export const PolicyTableRow = memo(function PolicyTableRow({
  policy,
  isSelected,
  onSelectChange,
  onInvoiceStatusChange,
  renewalTracking,
  onRenewalStatusChange,
  visibleColumns,
}: IPolicyTableRowProps) {
  const navigate = useNavigate();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/insurance/policies/${policy._id}`);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`View policy ${policy.policyNumber}`}
      className="flex items-center h-14 px-6 border-b border-table-row-border hover:bg-muted/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset min-w-max"
      onClick={() => navigate(`/insurance/policies/${policy._id}`)}
      onKeyDown={handleKeyDown}
    >
      {/* Checkbox — always visible, sticky left */}
      <div
        className="w-[48px] shrink-0 pr-2 flex items-center sticky left-0 bg-table-container z-10"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={checked => onSelectChange(checked === true)}
          aria-label={`Select policy ${policy.policyNumber}`}
        />
      </div>

      {visibleColumns.map(col => (
        <div key={col.id} className={`${col.width ?? 'w-[120px]'} shrink-0 pr-3`}>
          {renderPolicyCell(
            col,
            policy,
            onInvoiceStatusChange,
            renewalTracking,
            onRenewalStatusChange
          )}
        </div>
      ))}
    </div>
  );
});
